import React from 'react';
import { Platform, Pressable, View, Text } from 'react-native';
import { Dialog, XStack, YStack } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

interface ExceededLimit {
  name: string;
  current: number;
  limit: number;
  percentage: number;
}

interface TrialExpirationModalProps {
  open: boolean;
  onClose: () => void;
  onSubscribe?: () => void;
  daysRemaining?: number;
  benefits?: string[];
  exceededLimits?: ExceededLimit[];
}

const TrialExpirationModal: React.FC<TrialExpirationModalProps> = ({
  open,
  onClose,
  onSubscribe,
  daysRemaining = 3,
  benefits = [
    'All your saved data and projects',
    'Access to all premium features',
    'Priority customer support',
    'Regular feature updates',
  ],
  exceededLimits = []
}) => {
  const theme = themeSelector();
  const hasExceededLimits = exceededLimits.length > 0;

  return (
    <Dialog key="trialExpiration" open={open}>
      <Dialog.Portal>
        <Dialog.Overlay
          animation="quick"
          opacity={0.6}
          style={{
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(8px)',
            })
          }}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
        <Dialog.Content
          bordered
          elevate
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          x={0}
          scale={1}
          style={[
            {
              padding: 0,
              minWidth: 500,
              maxWidth: 600,
              maxHeight: "85%",
              borderRadius: 24,
              overflow: 'hidden',
              elevation: 12,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 12 },
              shadowOpacity: 0.15,
              shadowRadius: 32,
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
              borderWidth: 1,
              borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
            },
            Platform.OS === 'web' && {
              backdropFilter: 'blur(24px)',
              boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
            }
          ]}
          opacity={1}
          y={0}
        >
          {/* Header */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 20,
            borderBottomWidth: 1,
            borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack alignItems="center" gap="$3">
              <View style={{
                backgroundColor: theme ? "#fef3c7" : "#451a03",
                borderRadius: 12,
                padding: 10,
              }}>
                <MaterialIcons
                  name="schedule"
                  size={20}
                  color={theme ? "#f59e0b" : "#fbbf24"}
                />
              </View>
              <YStack flex={1}>
                <Dialog.Title style={{
                  fontSize: 20,
                  fontWeight: '700',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  marginBottom: 4,
                  margin: 0,
                }}>
                  Your Trial Ends Soon
                </Dialog.Title>
                <Dialog.Description style={{
                  fontSize: 14,
                  color: theme ? '#64748b' : '#94a3b8',
                  fontWeight: '500',
                  margin: 0,
                }}>
                  {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining in your free trial
                </Dialog.Description>
              </YStack>
            </XStack>
          </View>

          {/* Content */}
          <View style={{ padding: 24 }}>
            <YStack gap="$4">
              <View style={{
                backgroundColor: theme ? '#f8fafc' : '#374151',
                borderRadius: 16,
                padding: 20,
                borderWidth: 1,
                borderColor: theme ? '#e5e7eb' : '#4b5563',
              }}>
                <YStack gap="$3">
                  <Text style={{
                    fontSize: 16,
                    fontWeight: '600',
                    color: theme ? '#1e293b' : '#f1f5f9',
                    marginBottom: 8,
                  }}>
                    Don't lose access to your work
                  </Text>
                  <Text style={{
                    fontSize: 14,
                    color: theme ? '#64748b' : '#94a3b8',
                    lineHeight: 22,
                  }}>
                    Your trial period is ending soon. Subscribe now to continue using all features and keep your data safe. We'll automatically save your progress.
                  </Text>
                  {hasExceededLimits && (
                    <View style={{
                      marginTop: 12,
                      padding: 12,
                      backgroundColor: theme ? '#fef2f2' : '#7f1d1d',
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: theme ? '#fecaca' : '#991b1b',
                    }}>
                      <XStack alignItems="center" gap="$2" marginBottom={8}>
                        <MaterialIcons
                          name="warning"
                          size={18}
                          color={theme ? "#dc2626" : "#fca5a5"}
                        />
                        <Text style={{
                          fontSize: 14,
                          fontWeight: '600',
                          color: theme ? '#dc2626' : '#fca5a5',
                        }}>
                          Data Limits Exceeded
                        </Text>
                      </XStack>
                      <Text style={{
                        fontSize: 12,
                        color: theme ? '#991b1b' : '#fecaca',
                        lineHeight: 18,
                        marginBottom: 8,
                      }}>
                        The following limits have been exceeded:
                      </Text>
                      <YStack gap="$1">
                        {exceededLimits.map((limit, index) => (
                          <XStack key={index} alignItems="center" gap="$2">
                            <MaterialIcons
                              name="error"
                              size={16}
                              color={theme ? "#dc2626" : "#fca5a5"}
                            />
                            <Text style={{
                              fontSize: 12,
                              color: theme ? '#991b1b' : '#fecaca',
                            }}>
                              {limit.name}: {limit.current} / {limit.limit} ({limit.percentage}%)
                            </Text>
                          </XStack>
                        ))}
                      </YStack>
                    </View>
                  )}
                  <View style={{
                    marginTop: 12,
                    padding: 12,
                    backgroundColor: theme ? '#eff6ff' : '#1e3a5f',
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: theme ? '#bfdbfe' : '#3b82c1',
                  }}>
                    <XStack alignItems="center" gap="$2" marginBottom={4}>
                      <MaterialIcons
                        name="info"
                        size={18}
                        color={theme ? "#2563eb" : "#93c5fd"}
                      />
                      <Text style={{
                        fontSize: 13,
                        fontWeight: '600',
                        color: theme ? '#1e40af' : '#bfdbfe',
                      }}>
                        After Trial Expires
                      </Text>
                    </XStack>
                    <YStack gap="$1" marginTop={4}>
                      <XStack alignItems="flex-start" gap="$2">
                        <MaterialIcons
                          name="block"
                          size={14}
                          color={theme ? "#2563eb" : "#93c5fd"}
                          style={{ marginTop: 2 }}
                        />
                        <Text style={{
                          fontSize: 12,
                          color: theme ? '#1e40af' : '#bfdbfe',
                          lineHeight: 18,
                          flex: 1,
                        }}>
                          You won't be able to add new data or share existing data
                        </Text>
                      </XStack>
                      <XStack alignItems="flex-start" gap="$2">
                        <MaterialIcons
                          name="lock"
                          size={14}
                          color={theme ? "#2563eb" : "#93c5fd"}
                          style={{ marginTop: 2 }}
                        />
                        <Text style={{
                          fontSize: 12,
                          color: theme ? '#1e40af' : '#bfdbfe',
                          lineHeight: 18,
                          flex: 1,
                        }}>
                          Access to premium features will be restricted
                        </Text>
                      </XStack>
                    </YStack>
                  </View>
                </YStack>
              </View>

              {/* Countdown Card */}
              <View style={{
                backgroundColor: theme ? "#fef3c7" : "#451a03",
                borderRadius: 12,
                padding: 16,
              }}>
                <XStack alignItems="center" justifyContent="center" gap="$3">
                  <MaterialIcons
                    name="timer"
                    size={24}
                    color={theme ? "#f59e0b" : "#fbbf24"}
                  />
                  <Text style={{
                    fontSize: 18,
                    fontWeight: '700',
                    color: theme ? '#f59e0b' : '#fbbf24',
                  }}>
                    {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} left
                  </Text>
                </XStack>
              </View>

              {/* Benefits */}
              <YStack gap="$2">
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  marginBottom: 8,
                }}>
                  What you'll keep:
                </Text>
                {benefits.map((benefit, index) => (
                  <XStack key={index} alignItems="center" gap="$2">
                    <MaterialIcons
                      name="check-circle"
                      size={20}
                      color={theme ? "#059669" : "#10b981"}
                    />
                    <Text style={{
                      fontSize: 14,
                      color: theme ? '#1e293b' : '#f1f5f9',
                    }}>
                      {benefit}
                    </Text>
                  </XStack>
                ))}
              </YStack>
            </YStack>
          </View>

          {/* Footer Actions */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 16,
            borderTopWidth: 1,
            borderTopColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack gap="$3" justifyContent="flex-end">
              <Dialog.Close asChild>
                <Pressable
                  onPress={onClose}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: theme ? '#f1f5f9' : '#4b5563',
                    borderWidth: 1,
                    borderColor: theme ? '#e2e8f0' : '#6b7280',
                    minWidth: 100,
                    alignItems: 'center',
                  }}
                >
                  <Text style={{
                    color: theme ? '#64748b' : '#d1d5db',
                    fontWeight: '600',
                    fontSize: 14,
                  }}>
                    Remind Me Later
                  </Text>
                </Pressable>
              </Dialog.Close>
              <Dialog.Close asChild>
                <Pressable
                  onPress={() => {
                    onClose();
                    onSubscribe?.();
                  }}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: theme ? '#3b82f6' : '#2563eb',
                    minWidth: 100,
                    alignItems: 'center',
                    elevation: 2,
                    shadowColor: '#3b82f6',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.2,
                    shadowRadius: 8,
                  }}
                >
                  <Text style={{
                    color: '#ffffff',
                    fontWeight: '600',
                    fontSize: 14,
                  }}>
                    Subscribe Now
                  </Text>
                </Pressable>
              </Dialog.Close>
            </XStack>
          </View>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

export default TrialExpirationModal;

